import { formatMoney } from "accounting-js";
import { targetTotalFinance } from "./quote";
import { msaTotalCfaInstallmentIncPayout } from "./quoteItem";
import { IS_NZ, CFNZ_FINANCE_TERM, CFNZ_FINANCE_SUBTYPE } from "../../constants";
import { topLevelHardwareItemsWithClickCharge } from "../selectors";
import {targetTotal, flatMonthlyChargeTotal, CashSaasItemsTargetPriceTotal} from './quote';
import { types } from "../actions/types";

export const cfaCcBwTotalCostExclGst = state =>
  Number(state.quote.finance.cfaCcBwServiceCopyCost + state.quote.finance.cfaCcBw).toFixed(4);

export const cfaCcColourTotalCostExclGst = state =>
  Number(state.quote.finance.cfaCcColourServiceCopyCost +
  (state.quote.finance.financeSubtype === "PPP_COMBINED"
    ? state.quote.finance.cfaCcBw
    : state.quote.finance.cfaCcColour)).toFixed(4);

export const cfaCcBwAgreedTotalClickVolume = state =>
  state.quote.finance.financeSubtype === "PPP_COMBINED"
    ? (state.quote.finance.cfaCcBwMinimumMonthlyClickVolume +
        state.quote.finance.cfaCcColourMinimumMonthlyClickVolume) *
      state.quote.finance.financeTerm
    : state.quote.finance.cfaCcBwMinimumMonthlyClickVolume *
      state.quote.finance.financeTerm;

export const cfaCcColourAgreedTotalClickVolume = state =>
  state.quote.finance.financeSubtype === "PPP_COMBINED"
    ? (state.quote.finance.cfaCcBwMinimumMonthlyClickVolume +
        state.quote.finance.cfaCcColourMinimumMonthlyClickVolume) *
      state.quote.finance.financeTerm
    : state.quote.finance.cfaCcColourMinimumMonthlyClickVolume *
      state.quote.finance.financeTerm;

export const cfaBaseCharge = state =>
state.quote.finance.financeSubtype === "PMP"
  ? state.quote.finance.cfaCcBwMinimumMonthlyClickVolume *
      cfaCcBwTotalCostExclGst(state) +
    state.quote.finance.cfaCcColourMinimumMonthlyClickVolume *
      cfaCcColourTotalCostExclGst(state)
  : 0;

export const userTotalClickVolumeBw = state => {
  if(!["PMP"].includes(state.quote.finance.financeSubtype)){
    return 0;
  }

  const hardwareItems = topLevelHardwareItemsWithClickCharge(state);
  if(!hardwareItems.length){
    return 0;
  }

  return hardwareItems.reduce((value, item) => value + Number(item.userClickVolumeBw), 0)
}

export const userTotalClickVolumeColour = state => {
  if(!["PMP"].includes(state.quote.finance.financeSubtype)){
    return 0;
  }

  const hardwareItems = topLevelHardwareItemsWithClickCharge(state);
  if(!hardwareItems.length){
    return 0;
  }

  return hardwareItems.reduce((value, item) => value + Number(item.userClickVolumeColour), 0)
}

export const recommendServiceCopyCostBw = state => {
  if(!["PMP"].includes(state.quote.finance.financeSubtype)){
    return 0;
  }

  const hardwareItems = topLevelHardwareItemsWithClickCharge(state);
  if(!hardwareItems.length){
    return 0;
  }

  const totalClickPerVolumeBw = hardwareItems.reduce((value, item) => value + Number(item.userClickVolumeBw * item.defaultCcBw), 0);
  const totalClickVolumeBw = userTotalClickVolumeBw(state);

  return totalClickVolumeBw > 0 ? Number(Math.abs(totalClickPerVolumeBw / totalClickVolumeBw).toFixed(4)) : 0;
}

export const recommendServiceCopyCostColour = state => {
  if(!["PMP"].includes(state.quote.finance.financeSubtype)){
    return 0;
  }

  const hardwareItems = topLevelHardwareItemsWithClickCharge(state);
  if(!hardwareItems.length){
    return 0;
  }

  const totalClickPerVolumeColour = hardwareItems.reduce((value, item) => value + Number(item.userClickVolumeColour * item.defaultCcColour), 0);
  const totalClickVolumeColour = userTotalClickVolumeColour(state);

  return totalClickPerVolumeColour > 0 ? Number(Math.abs(totalClickPerVolumeColour / totalClickVolumeColour).toFixed(4)) : 0;
}

export const cfaCcBwExcessTotalCostExclGst = state =>
state.quote.finance.financeSubtype === "PMP"
  ? state.quote.finance.cfaCcBwExcessFinancedCopyCost +
    state.quote.finance.cfaCcBwServiceCopyCost
  : 0;

export const cfaCcColourExcessTotalCostExclGst = state =>
  state.quote.finance.financeSubtype === "PMP"
    ? state.quote.finance.cfaCcColourExcessFinancedCopyCost +
      state.quote.finance.cfaCcColourServiceCopyCost
    : 0;

export const isCfaRentOrLease = ({ quote }) =>
  quote.finance.financeType === "CFA" &&
  ["RENTAL", "LEASE"].includes(quote.finance.financeSubtype);

export const isPPPOrPMP =  ({ quote }) =>
  quote.finance.financeType === "CFA" &&
  ["PMP", "PPP_COMBINED", "PPP_STANDARD"].includes(quote.finance.financeSubtype);

export function isCfa({ quote }) {
  return quote.finance.financeType === "CFA";
}

export function isCfnz({ quote }) {
  return quote.finance.financeType === "CFNZ";
}

export function isLease({ quote }) {
  return quote.finance.financeSubtype === "LEASE";
}

// "PPP" stands for "Pay Per Print"
export function isPppOrPmp(state) {
  return isPppCombined(state) || isPppStandard(state) || isPmp(state);
}

export function isPppCombined({ quote }) {
  return quote.finance.financeSubtype === "PPP_COMBINED";
}

export function isPppStandard({ quote }) {
  return quote.finance.financeSubtype === "PPP_STANDARD";
}

//treat PMP as PPP
export function isPmp({ quote }) {
  return quote.finance.financeSubtype === "PMP";
}

export function isOriginatorReferenceValid({ quote }) {
  return /^\d+-\d+$/.test(quote.finance.originatorReference);
}

export function totalPayout(state) {
  return Math.max(
    state.quote.finance.canonFinancePayout +
      state.quote.finance.thirdPartyPayout,
    0
  );
}

// Finance numbers must be non-negative
export const validateNonNegativeFinanceFields = state => {
  if (state.quote.finance.financeType === "CFA" || state.quote.finance.financeType === "CFNZ") {
    return [
      {
        key: "canonFinancePayout",
        label: "Canon Finance Payout"
      },
      {
        key: "thirdPartyPayout",
        label: "Third Party Payout"
      },
      {
        key: "totalAmountFinanced",
        label: "Total Amount Financed"
      },
      {
        key: "cfaAmount",
        label: "CF Amount"
      },
      {
        key: "financeTerm",
        label: "Finance Term"
      },
      {
        key: "instalmentAmount",
        label: "Fixed Rental Installment excl. GST"
      }
    ]
      .filter(({ key }) => Number(state.quote.finance[key]) < 0)
      .map(({ label }) => `${label} must be non-negative.`);
  } else {
    return [];
  }
};
// Required finance fields
export const validateRequiredFinanceFields = state => {
  if (state.quote.finance.financeType === "CFA" || state.quote.finance.financeType === "CFNZ") {
    const requiredFinanceFields = state.quote.isMsa
      ? [
          {
            key: "canonFinancePayout",
            label: "Canon Finance Payout"
          },
          {
            key: "thirdPartyPayout",
            label: "Third Party Payout"
          }
        ]
      : [
          {
            key: "totalAmountFinanced",
            label: "Total Amount Financed"
          },
          {
            key: "cfaAmount",
            label: "CF Amount"
          },
          {
            key: "financeTerm",
            label: "Finance Term"
          },
          {
            key: "instalmentAmount",
            label: "Fixed Rental Installment excl. GST"
          }
        ];

    return requiredFinanceFields
      .filter(({ key }) => state.quote.finance[key] === "")
      .map(({ label }) => `${label} is required.`);
  } else {
    return [];
  }
};

export function validateDataFromOriginator(state) {
  const messages = [];

  if (
    (state.quote.finance.financeType === "CFA" || state.quote.finance.financeType === "CFNZ") &&
    !state.quote.isMsa &&
    state.quote.finance.originatorReference &&
    state.quote.finance.isConfigured
  ) {
    const formattedCfaAmount = formatMoney(state.quote.finance.cfaAmount);
    const formattedTargetTotal = formatMoney(targetTotalFinance(state));

    // CF Amount must equal Target Total for Originator
    if (formattedCfaAmount !== formattedTargetTotal) {
      messages.push(
        `The CF Amount/Amt Financed (${formattedCfaAmount} ) from the Originator quote option ${state.quote.finance.originatorReference} does not equal to the Total Price (${formattedTargetTotal}) of the Spark order quote. Please update the Originator quote option and retry, or select another quote option`
      );
    }

    if (
      state.quote.finance.financeType === "CFA" &&
      !["RENTAL", "LEASE", "PPP_STANDARD", "PPP_COMBINED", "PMP"].includes(
        state.quote.finance.financeSubtype
      )
    ) {
      messages.push("The subtype in the finance quote is not supported");
    }

    if (
      state.quote.finance.financeType === "CFNZ" &&
      !CFNZ_FINANCE_SUBTYPE.includes(
        state.quote.finance.financeSubtype
      )
    ) {
      messages.push("The subtype in the finance quote is not supported");
    }

    if (
      !["MONTHLY", "QUARTERLY", "YEARLY"].includes(
        state.quote.finance.instalmentType
      )
    ) {
      messages.push(
        `The finance installment type "${state.quote.finance.instalmentType}" is not supported by Spark. Please select a different Finance Quote to use, or create a manual order for your Finance Quotes.`
      );
    }
  }

  return messages;
}

export function validateIfFinanceConfigured(state) {
  return (state.quote.finance.financeType === "CFA" || state.quote.finance.financeType === "CFNZ") &&
    !state.quote.isMsa &&
    !state.quote.finance.isConfigured
    ? "Financing details need to be loaded from Originator."
    : "";
}

// As known as "Installment Amount" as well
export const theMsaInstallmentValue = state => {
  if (isCfa(state) && state.quote.isMsa) {
    return state.quote.items
      .map(
        ({ targetPrice, msaCfa, qty }) =>
          msaTotalCfaInstallmentIncPayout(state, targetPrice, msaCfa) * qty
      )
      .reduce((sum, installment) => sum + installment, 0);
  } else {
    return 0;
  }
};

const msaPayableMethodSet = state =>
  new Set(state.quote.items.map(item => item.msaPayableMethod));

export const isMsaPayableMethodValid = state =>
  msaPayableMethodSet(state).size === 1 || !isCfa(state) || !state.quote.isMsa;

export const theMsaPayableMethod = state =>
  isCfa(state) && state.quote.isMsa && isMsaPayableMethodValid(state)
    ? msaPayableMethodSet(state)
        .values()
        .next().value
    : "";

const msaInstalmentFrequencySet = state =>
  new Set(state.quote.items.map(item => item.msaInstalmentFrequency));

export const isMsaInstalmentFrequencyValid = state =>
  msaInstalmentFrequencySet(state).size === 1 ||
  !isCfa(state) ||
  !state.quote.isMsa;

export const theMsaInstalmentFrequency = state =>
  isCfa(state) && state.quote.isMsa && isMsaInstalmentFrequencyValid(state)
    ? msaInstalmentFrequencySet(state)
        .values()
        .next().value
    : "";

const msaTermSet = state =>
  new Set(state.quote.items.map(item => Number(item.msaTerm)));

export const isMsaTermValid = state =>
  msaTermSet(state).size === 1 || !isCfa(state) || !state.quote.isMsa;

export const theMsaTerm = state =>
  isCfa(state) && state.quote.isMsa && isMsaTermValid(state)
    ? msaTermSet(state)
        .values()
        .next().value
    : "";

/********************************* TMC related functions *********************************/
//this is the function to calculate all the variations
export const totalMinimumCharge = state => {
  if(IS_NZ) {
    return 0;
  }

  if(state.quote.finance.financeType === "CASH"){
    return cashTmc(state);
  }

  if(isCfaRentOrLease(state)){
    return rentLeaseTmc(state);
  }

  if(isPmp(state)){
    return PmpTmc(state); 
  }

  if(isPppStandard(state)){
    return pppTmc(state);
  }

  if(isPppCombined(state)){
    return pppCmbTmc(state);
  }

  return 0;
}

export function cashTmc(state) {
  const totalHmc = flatMonthlyChargeTotal(state);
  const totalTarget = targetTotal(state)
  const tmcTotal = totalTarget + (totalHmc * state.quote.serviceTerm);
  return tmcTotal.toFixed(2);
}

export function rentLeaseTmc(state) {
  const rentInstallment = state.quote.finance.instalmentAmount;
  const term = getTermForTmc(state);
  const rentInstalmentTotal = Number(rentInstallment * term);
  const totalHmc = flatMonthlyChargeTotal(state) * state.quote.finance.financeTerm;
  const cashSaasTotal = CashSaasItemsTargetPriceTotal(state);
  const tmcTotal = rentInstalmentTotal + totalHmc + cashSaasTotal;
  return tmcTotal.toFixed(2);
}

const flagSettingForTmc = {
  "V": {"percentage": 1.15, "num_skip_year": 1},
  "T": {"percentage": 1.1, "num_skip_year": 1},
  "L": {"percentage": 1.075, "num_skip_year": 1},
  "F": {"percentage": 1.05, "num_skip_year": 1},
  "K": {"percentage": 1.04, "num_skip_year": 1},
  "J": {"percentage": 1.03, "num_skip_year": 1},
  "7": {"percentage": 1.05, "num_skip_year": 3},
  "6": {"percentage": 1.2, "num_skip_year": 3},
  "5": {"percentage": 1.1, "num_skip_year": 3},
  "N": {"percentage": 1, "num_skip_year": 0},
};

export function PmpTmc(state) {
  const priceFlag = state.quote.priceFlag;
  if(priceFlag === 'M'){
    return PmpFixTmc(state)
  }else if(flagSettingForTmc[priceFlag] !== undefined){
    const percentage = flagSettingForTmc[priceFlag]["percentage"]
    const skipYear = flagSettingForTmc[priceFlag]["num_skip_year"]
    return PmpVariableTmc(state, percentage, skipYear)
  }
  return -1; //incorrect tmc value for invalid flag
}

const PmpFixTmc = (state) => {
  const baseCharge = (cfaBaseCharge(state) / 100).toFixed(2);
  const term = getTermForTmc(state);
  const cashSaasTotal = CashSaasItemsTargetPriceTotal(state);
  return ((baseCharge * term) + cashSaasTotal).toFixed(2);
}

const PmpVariableTmc = (state, percentage, skipYear) => {
  const installment = state.quote.finance.instalmentAmount;
  const bwServiceCharge = state.quote.finance.cfaCcBwMinimumMonthlyClickVolume * (state.quote.finance.cfaCcBwServiceCopyCost / 100);
  const ccServiceCharge = state.quote.finance.cfaCcColourMinimumMonthlyClickVolume * (state.quote.finance.cfaCcColourServiceCopyCost / 100);
  let serviceCharge = bwServiceCharge + ccServiceCharge;
  const term = getTermForTmc(state);
  const periodDivisor = state.quote.finance.instalmentType === types.INSTALMENT_TYPE_MONTHLY ? 12 : 4;
  let tmcTotal = 0
  
  for(let i=term; i > 0; i -= periodDivisor){
    if(skipYear){
      skipYear--
    }else{
      serviceCharge = serviceCharge * percentage;
    }

    const yearlyTerm = (i/periodDivisor) >= 1 ? periodDivisor : (i % periodDivisor)
    const yearlyBaseCharge= installment + serviceCharge
    tmcTotal += (yearlyBaseCharge * yearlyTerm)
  }
  const cashSaasTotal = CashSaasItemsTargetPriceTotal(state);
  tmcTotal += cashSaasTotal;

  return tmcTotal.toFixed(2)
}

export function pppTmc(state) {
  const priceFlag = state.quote.priceFlag;
  if(priceFlag === 'M'){
    return pppFixTmc(state)
  }else if(flagSettingForTmc[priceFlag] !== undefined){
    const percentage = flagSettingForTmc[priceFlag]["percentage"]
    const skipYear = flagSettingForTmc[priceFlag]["num_skip_year"]
    return pppVariableTmc(state, percentage, skipYear)
  }
  return -1; //incorrect tmc value for invalid flag
}

const pppFixTmc = (state) => {
  const term = getTermForTmc(state);
  //click charges are based on cent
  const bwTotal = ((state.quote.finance.cfaCcBwMinimumMonthlyClickVolume * cfaCcBwTotalCostExclGst(state) * term)/100);
  const colTotal = ((state.quote.finance.cfaCcColourMinimumMonthlyClickVolume * cfaCcColourTotalCostExclGst(state) * term)/100);
  const installment = state.quote.finance.instalmentAmount * term;

  return (bwTotal + colTotal + installment).toFixed(2)
}

const pppVariableTmc = (state, percentage, skipYear) => {
  const term = getTermForTmc(state);
  const installment = state.quote.finance.instalmentAmount * term;
  const periodDivisor = state.quote.finance.instalmentType === types.INSTALMENT_TYPE_MONTHLY ? 12 : 4;
  const rentBwCost = state.quote.finance.cfaCcBw;
  let serviceBwCost = state.quote.finance.cfaCcBwServiceCopyCost;
  const bwClickVol = state.quote.finance.cfaCcBwMinimumMonthlyClickVolume;
  const rentColCost = state.quote.finance.cfaCcColour;
  let serviceColCost = state.quote.finance.cfaCcColourServiceCopyCost;
  const colClickVol = state.quote.finance.cfaCcColourMinimumMonthlyClickVolume;
  let tmcTotal = 0

  for(let i=term; i > 0; i -= periodDivisor){
    if(skipYear){
      skipYear--
    }else{
      serviceBwCost = serviceBwCost * percentage;
      serviceColCost = serviceColCost * percentage;
    }

    const yearlyTerm = (i/periodDivisor) >= 1 ? periodDivisor : (i % periodDivisor)
    const yearlyBwCharge = ((rentBwCost + serviceBwCost) * bwClickVol)/100 * yearlyTerm ;
    const yearlyColourCharge = ((rentColCost + serviceColCost) * colClickVol)/100 * yearlyTerm ;
    tmcTotal += yearlyBwCharge + yearlyColourCharge
  }
  tmcTotal += installment; //fixed rental installment amount
  
  return tmcTotal.toFixed(2)
}

export function pppCmbTmc(state) {
  const rentBwCost = state.quote.finance.cfaCcBw / 100; //bw and col values are the same in pppCombine and change it to cent
  const volumeTotal = state.quote.finance.cfaCcBwMinimumMonthlyClickVolume + state.quote.finance.cfaCcColourMinimumMonthlyClickVolume;
  const term = getTermForTmc(state);
  const rentTotal = (rentBwCost * volumeTotal) * term;
  const installment = state.quote.finance.instalmentAmount * term;
  const tmcTotal = rentTotal + installment;

  return tmcTotal.toFixed(2);
}

const getTermForTmc = (state) => {
  const originalMonth = state.quote.finance.financeTerm;
  const deferredMonth = state.quote.finance.deferredPayment;
  const finalMonth = originalMonth - deferredMonth;
  const instalmentType = state.quote.finance.instalmentType;
  if(instalmentType === types.INSTALMENT_TYPE_MONTHLY){
    return finalMonth
  }
  return Number(finalMonth/3).toFixed(2)
}